import FilterForm from 'components/forms/FilterForm';
import dayjs from 'dayjs';
import { BulkDocumentsDownload } from './BulkDocumentsDownload';
import { CrudFilters, HttpError } from '@refinedev/core';
import {
  DatePicker,
  Form,
  FormProps,
  Input,
  Select,
  Space,
  Table,
  Tooltip,
  Typography
} from 'antd';
import {
  EditButton,
  ShowButton,
  TagField,
  TextField,
  useSelect,
  useTable
} from '@refinedev/antd';
import { IDocumentation } from 'interfaces/documentation/documentations';
import { IGraduateCourse } from 'interfaces/graduate_courses';
import { SearchOutlined } from '@ant-design/icons';
import { formatDate } from 'services/date';
import {
  getStatusColor,
  translateDocumentationStatus
} from 'services/documentations';
import { useState } from 'react';

interface IFilter {
  courseId: string;
  byStatus: string;
  byName: string;
  byEmail: string;
  byDocumentInfo: string;
  byBeforeDate: string;
  byAfterDate: string;
}

interface FilterFormProps {
  searchFormProps: FormProps<IFilter>;
  onCourseNameChange: (name?: string) => void;
}

// A lista abre filtrada por "Submetido": é o que a secretaria tem para validar.
const DEFAULT_STATUS = 'submitted';

const Filter = ({ searchFormProps, onCourseNameChange }: FilterFormProps) => {
  const { form } = searchFormProps;

  // resetFields() devolveria byStatus a 'submitted' (o initialValues abaixo);
  // aqui "Limpar filtro" significa listar tudo, então zera campo a campo.
  const handleClear = () => {
    if (!form) {
      return;
    }

    form.setFieldsValue(
      Object.keys(form.getFieldsValue()).reduce(
        (fields, name) => ({ ...fields, [name]: undefined }),
        {}
      )
    );
    form.submit();
  };

  const { selectProps: courseSelectProps } = useSelect<IGraduateCourse>({
    resource: 'graduate_courses',
    optionLabel: 'name',
    optionValue: 'id',

    onSearch: value => [
      {
        field: 'filter[name]',
        operator: 'eq',
        value
      }
    ],

    pagination: {
      mode: 'server'
    }
  });

  return (
    <FilterForm
      searchFormProps={{
        ...searchFormProps,
        initialValues: { byStatus: DEFAULT_STATUS }
      }}
      onClear={handleClear}
    >
      <Form.Item name="byName">
        <Input placeholder="Nome" suffix={<SearchOutlined />} />
      </Form.Item>

      <Form.Item name="byEmail">
        <Input placeholder="E-mail do aluno" />
      </Form.Item>

      <Form.Item name="byDocumentInfo">
        <Input placeholder="CPF" />
      </Form.Item>

      <Form.Item name="courseId">
        <Select
          placeholder="Nome do curso"
          allowClear
          {...courseSelectProps}
          // O nome vai para a confirmação do download em lote; buscar o curso
          // pelo id exigiria graduate_courses:show, que a secretaria pode não ter.
          onChange={(_, option) =>
            onCourseNameChange(
              (Array.isArray(option) ? undefined : option?.label)?.toString()
            )
          }
        />
      </Form.Item>

      <Form.Item name="byAfterDate">
        <DatePicker
          className="w-full"
          placeholder="Início a partir de"
          format="DD/MM/YYYY"
          picker="date"
        />
      </Form.Item>

      <Form.Item name="byBeforeDate">
        <DatePicker
          className="w-full"
          placeholder="Término em"
          format="DD/MM/YYYY"
          picker="date"
        />
      </Form.Item>

      <Form.Item name="byStatus">
        <Select placeholder="Status" allowClear>
          <Select.Option key={'pending'}>Pendente</Select.Option>
          <Select.Option key={'submitted'}>Submetido</Select.Option>
          <Select.Option key={'approved'}>Aprovado</Select.Option>
          <Select.Option key={'rejected'}>Rejeitado</Select.Option>
        </Select>
      </Form.Item>
    </FilterForm>
  );
};

const Audit = () => {
  const { tableProps, searchFormProps } = useTable<
    IDocumentation,
    HttpError,
    IFilter
  >({
    onSearch: params => {
      const filters: CrudFilters = [];
      const {
        courseId,
        byStatus,
        byName,
        byEmail,
        byDocumentInfo,
        byAfterDate,
        byBeforeDate
      } = params;

      filters.push(
        {
          field: 'filter[by_course]',
          operator: 'eq',
          value: courseId
        },
        {
          field: 'filter[by_status]',
          operator: 'eq',
          value: byStatus
        },
        {
          field: 'filter[by_name]',
          operator: 'eq',
          value: byName
        },
        {
          field: 'filter[by_email]',
          operator: 'eq',
          value: byEmail
        },
        {
          field: 'filter[by_document_info]',
          operator: 'eq',
          value: byDocumentInfo
        },
        {
          field: 'filter[enrollment_created_after]',
          operator: 'eq',
          value: byAfterDate && dayjs(byAfterDate).format('YYYY-MM-DD')
        },
        {
          field: 'filter[enrollment_created_before]',
          operator: 'eq',
          value: byBeforeDate && dayjs(byBeforeDate).format('YYYY-MM-DD')
        }
      );

      return filters;
    },

    filters: {
      initial: [
        {
          field: 'filter[by_status]',
          operator: 'eq',
          value: DEFAULT_STATUS
        }
      ]
    }
  });

  // O curso escolhido no filtro, aplicado ou não: o download não depende da tabela, e o
  // status do filtro (documentação) não é o recorte do ZIP (documento aprovado).
  const selectedCourseId = Form.useWatch('courseId', searchFormProps.form);
  const [selectedCourseName, setSelectedCourseName] = useState<string>();

  return (
    <>
      <Filter
        searchFormProps={searchFormProps}
        onCourseNameChange={setSelectedCourseName}
      />
      <div className="flex justify-end mb-4">
        <BulkDocumentsDownload
          courseId={selectedCourseId}
          courseName={selectedCourseName}
        />
      </div>
      <Table {...tableProps} rowKey="id">
        <Table.Column<IDocumentation>
          dataIndex={['parent', 'user']}
          key="user_name"
          title="Informações do aluno"
          render={(_, record) => {
            return (
              <Space direction="vertical">
                <Space direction="horizontal">
                  <Typography.Text strong>Nome:</Typography.Text>
                  <TextField
                    value={
                      record.parent?.user?.full_name_or_name || 'Não informado'
                    }
                  />
                </Space>

                <Space direction="horizontal">
                  <Typography.Text strong>E-mail:</Typography.Text>
                  <TextField
                    value={record.parent?.user?.email || 'Não informado'}
                  />
                </Space>

                <Space direction="horizontal">
                  <Typography.Text strong>CPF:</Typography.Text>
                  <TextField
                    value={`${record.user_document_info || 'Não informado'}`}
                  />
                </Space>

                <Space direction="horizontal">
                  <Typography.Text strong>Data de início:</Typography.Text>
                  <TextField
                    value={
                      record.parent?.created_at
                        ? formatDate(
                            record.parent.created_at,
                            'DD/MM/YYYY - HH:mm'
                          )
                        : 'Não informado'
                    }
                  />
                </Space>
              </Space>
            );
          }}
        />
        <Table.Column
          dataIndex={['parent', 'course', 'name']}
          key="course_name"
          title="Curso"
          render={value => <TextField value={value} />}
        />
        <Table.Column<IDocumentation>
          key="total"
          title="Documentos"
          render={(_, record) => {
            const {
              approved_documents_count,
              rejected_documents_count,
              pending_documents_count
            } = record;

            return (
              <>
                <Space align="start" direction="vertical">
                  <TextField
                    value={`✅ ${approved_documents_count} deferidos`}
                  />

                  <TextField
                    value={`❌ ${rejected_documents_count} indeferidos`}
                  />

                  <TextField
                    value={`🕒 ${pending_documents_count} aguardando validação`}
                  />
                </Space>
              </>
            );
          }}
        />
        <Table.Column
          dataIndex="status"
          key="status"
          title="Status"
          render={value => (
            <TagField
              color={getStatusColor(value)}
              value={translateDocumentationStatus(value)}
            />
          )}
        />

        <Table.Column
          dataIndex="updated_at"
          key="updated_at"
          title="Atualizado em"
          sorter
          render={value => (
            <TextField value={formatDate(value, 'DD/MM/YYYY - HH:mm')} />
          )}
        />

        <Table.Column<IDocumentation>
          title="Ações"
          dataIndex="actions"
          render={(_, record) => {
            return (
              <Space>
                <Tooltip title="Validar documentos">
                  <EditButton hideText size="small" recordItemId={record.id} />
                </Tooltip>
                <Tooltip title="Visualizar detalhes">
                  <ShowButton hideText size="small" recordItemId={record.id} />
                </Tooltip>
              </Space>
            );
          }}
        />
      </Table>
    </>
  );
};

export default Audit;
